// One signed receipt (FR-21): what changed, how, when and with which key, plus
// a download the person can keep or give to anyone to check.
import { Alert, Anchor, Button, Code, Group, Loader, Paper, Table, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { api } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { SignedIn } from "../../shared/session/guards";
import { FieldBadges, useFormat } from "../../shared/ui/format";
import { FormError, WidePage } from "../../shared/ui/Page";
import { downloadJSON, receiptFile } from "./receiptFile";

export function ReceiptDetailPage() {
  return (
    <SignedIn>
      <ReceiptDetail />
    </SignedIn>
  );
}

function ReceiptDetail() {
  const { t } = useTranslation();
  const f = useFormat();
  const { id = "" } = useParams();
  const receipt = useQuery({
    queryKey: ["receipts", id],
    queryFn: () => unwrap(api.GET("/consents/receipts/{event_id}", { params: { path: { event_id: id } } })),
  });
  const [verdict, setVerdict] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const r = receipt.data;

  const check = async () => {
    if (!r) return;
    setError(null);
    const { data, error } = await api.POST("/public/receipts/verify", { body: receiptFile(r) });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setVerdict(data.valid);
  };

  return (
    <WidePage title={t("receipts.detail_title")}>
      <Anchor component={Link} to="/receipts" size="sm">
        {t("receipts.back")}
      </Anchor>
      {receipt.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : !r ? (
        <FormError message={errorMessage(receipt.error, t("error.generic"))} />
      ) : (
        <>
          <Paper withBorder p="md" radius="md">
            <Table>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th scope="row">{t("receipts.company")}</Table.Th>
                  <Table.Td data-testid="receipt-company">{r.company.name}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th scope="row">{t("receipts.time")}</Table.Th>
                  <Table.Td>{f.dateTime(r.created_at)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th scope="row">{t("receipts.before")}</Table.Th>
                  <Table.Td>
                    <FieldBadges fields={r.fields_before} color="gray" />
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th scope="row">{t("receipts.after")}</Table.Th>
                  <Table.Td data-testid="receipt-after">
                    <FieldBadges fields={r.fields_after} />
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th scope="row">{t("receipts.purposes")}</Table.Th>
                  <Table.Td>{r.purposes.length ? r.purposes.join(", ") : t("consents.any_purpose")}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th scope="row">{t("receipts.channel")}</Table.Th>
                  <Table.Td>{f.channel(r.channel)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th scope="row">{t("receipts.terms")}</Table.Th>
                  <Table.Td>{r.terms_version}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th scope="row">{t("receipts.kid")}</Table.Th>
                  <Table.Td>
                    <Code>{r.kid}</Code>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th scope="row">{t("receipts.event")}</Table.Th>
                  <Table.Td>
                    <Code>{r.event_id}</Code>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>
          <Group>
            <Button onClick={() => downloadJSON(`receipt-${r.event_id}.json`, receiptFile(r))}>
              {t("receipts.download")}
            </Button>
            <Button variant="light" onClick={() => void check()}>
              {t("receipts.check")}
            </Button>
          </Group>
          {verdict !== null && (
            <Alert color={verdict ? "green" : "red"} role="status" data-testid="receipt-verdict">
              {verdict ? t("verify.valid") : t("verify.invalid")}
            </Alert>
          )}
          <FormError message={error} />
          <Text size="sm" c="dimmed">
            {t("receipts.download_hint")}{" "}
            <Anchor component={Link} to="/verify-receipt">
              {t("receipts.verify_link")}
            </Anchor>
          </Text>
        </>
      )}
    </WidePage>
  );
}
